/**
 * The state behind an AskUserQuestion card: which options are ticked, what was
 * typed, which question of a batch is showing, and the submit / skip rules.
 *
 * Shared by the ledger card (Workshop) and the M7 rail's grouped card, so both
 * answer the same way: nothing sends until the explicit submit, a pick-one
 * question retires any typed answer, and a batch moves the user on to the next
 * unanswered question.
 */
import { useState } from "react";
import {
  formatAnswersMessage,
  isAnswerComplete,
  SKIP_QUESTIONS_MESSAGE,
  type AskUserQuestion,
  type AskUserQuestionAnswer,
} from "./askUserQuestion";

export const emptyAnswer = (): AskUserQuestionAnswer => ({ selected: [], freeText: "" });

export const isAnswered = (answer: AskUserQuestionAnswer | undefined): boolean =>
  (answer?.selected.length ?? 0) > 0 || (answer?.freeText.trim().length ?? 0) > 0;

export function useAskUserQuestionState(
  questions: AskUserQuestion[],
  onSubmit: ((message: string) => void) | undefined,
  disabled: boolean,
) {
  const [answers, setAnswers] = useState<AskUserQuestionAnswer[]>(() =>
    questions.map(emptyAnswer),
  );
  const [active, setActive] = useState(0);
  const [submitted, setSubmitted] = useState(false);

  // Locked but still shown as a form: the turn is mid-flight, or the answers are
  // on their way and the transcript hasn't caught up yet.
  const locked = disabled || submitted || !onSubmit;
  const single = questions.length === 1;
  const answeredCount = answers.filter(isAnswered).length;
  const complete = isAnswerComplete(questions, answers);
  const activeIndex = Math.min(active, questions.length - 1);
  const question = questions[activeIndex]!;
  const answer = answers[activeIndex] ?? emptyAnswer();
  const typed = answer.freeText.trim().length > 0;

  const submit = (next: AskUserQuestionAnswer[] = answers) => {
    if (locked) return;
    setSubmitted(true);
    onSubmit?.(formatAnswersMessage(questions, next));
  };

  /** The way out. The composer stays shut until the card is answered or skipped. */
  const skip = () => {
    if (locked) return;
    setSubmitted(true);
    onSubmit?.(SKIP_QUESTIONS_MESSAGE);
  };

  /** The next question still needing an answer, searching forward then wrapping. */
  const nextUnanswered = (from: number, state: AskUserQuestionAnswer[]): number | null => {
    for (let step = 1; step <= questions.length; step++) {
      const i = (from + step) % questions.length;
      if (!isAnswered(state[i])) return i;
    }
    return null;
  };

  const updateAnswer = (
    index: number,
    update: (current: AskUserQuestionAnswer) => AskUserQuestionAnswer,
  ): AskUserQuestionAnswer[] => {
    const next = answers.map((a, i) => (i === index ? update(a) : a));
    setAnswers(next);
    return next;
  };

  const toggleOption = (index: number, label: string) => {
    if (locked) return;
    const target = questions[index]!;
    const next = updateAnswer(index, (current) => {
      // Single-select: picking is exclusive, and it retires any typed answer.
      if (!target.multiSelect) return { selected: [label], freeText: "" };
      return current.selected.includes(label)
        ? { ...current, selected: current.selected.filter((l) => l !== label) }
        : { ...current, selected: [...current.selected, label] };
    });
    // A pick-one question is finished the moment it's picked, so move the user on.
    // Multi-select stays put — they may still be ticking boxes.
    if (!single && !target.multiSelect) {
      const jump = nextUnanswered(index, next);
      if (jump !== null) setActive(jump);
    }
  };

  const setFreeText = (index: number, value: string) => {
    if (locked) return;
    const target = questions[index]!;
    updateAnswer(index, (current) => ({
      // Typing into a pick-one question replaces the pick rather than adding to it.
      selected: target.multiSelect || !value.trim() ? current.selected : [],
      freeText: value,
    }));
  };

  const onFreeTextEnter = () => {
    if (complete) {
      submit();
      return;
    }
    const jump = nextUnanswered(activeIndex, answers);
    if (jump !== null) setActive(jump);
  };

  // Multi-select never auto-advances (user may still be ticking), so expose an
  // explicit Next once this question has an answer and others remain.
  const jumpTarget = !single && isAnswered(answer) ? nextUnanswered(activeIndex, answers) : null;
  const goNext = () => {
    if (jumpTarget === null) return;
    setActive(jumpTarget);
  };

  return {
    answers,
    setActive,
    locked,
    single,
    answeredCount,
    complete,
    activeIndex,
    question,
    answer,
    typed,
    jumpTarget,
    submit,
    skip,
    toggleOption,
    setFreeText,
    onFreeTextEnter,
    goNext,
  };
}
